// TODO: Add utility functions in this file
import { createOption, DATE_RANGE_OPTIONS_DISPLAY } from 'components/Common/DateRangePicker';
import { AppConfiguration } from 'config';
import { addDays, addHours, addMonths, differenceInMinutes, isAfter, isBefore, isSameMonth, subDays, subHours, subMinutes } from 'date-fns';
import _ from 'lodash';
import { UI_PERMISSION_KEY } from 'pages/Roles/newFeaturesAndPermissionsData';
import React from 'react';
import CurrencyFormat from 'react-currency-format';
import { Alert, UncontrolledTooltip } from 'reactstrap';
import * as CONSTANTS from '../common/utils/data_validations';
import { bsh_getAdminUserDetails, bsh_getClientUserDetails, bsh_getInsuranceIoUserDetails } from './sf_browser_storage_helper';
import { ADMIN_ONLY_COLUMN, ADMIN_USER_PERMISSIONS, ANALYTICS_TIMEFRAME_GROUP_BY_KEYS, BUDGETS_TYPES, ERR_DEVISE_SIGNATURE_VERIFICATION_FAILED, FILTER_COMPONENT_TYPES, GENERIC_ERROR_MESSAGE, getLeadTypeByName, HTML_SNIPPET_TYPE, LEAD_REFUND_DELAY_FOR_STATUS_IN_MINUTES, LEAD_TYPE_IDS, NAME_OF_PRODUCT_TYPES, PRODUCT_TYPE_IDS, QUOTE_FUNNEL_QUESTION_DEPENDS_ON_TYPE, REPORTING_TIMEZONE, STATUS_TYPES, TABS_CONSTANTS, TIMEZONE_NAMES, ZIP_TIER, SIC8_CODE, SIC_CODE, NAICS_CODE } from './sf_constants';
import { BROWSER_IANA_TIMEZONE, formatDatePST, getZonedEndOfDay, getZonedEndOfMonth, getZonedStartOfDay, getZonedStartOfMonth } from './time_utils';
import jwt_decode from "jwt-decode";
import { DEFAULT_FILTERD_URL_PARAMS, NAME_OF_CURRENT_ACCOUNT_COLUMN, ORDER_BY, PLATFORM_SETTINGS_FEATURES } from 'common/sf_strings';
import { leftSideBarThemeTypes } from 'constants/layout';
import LoadingSpinner from 'components/Common/LoadingSpinner';
import SfLoadingSpinner from 'components/Common/SfLoadingSpinner';
import { getFilterPackagesByProductAndLeadType, getSfFilterByProductAndLeadType } from './sf_campaign_helper';
import { parseISO } from 'date-fns';
import AdminOnlyIcon from 'components/Common/AdminOnlyIcon';
import ToastAlert, { ToastAlertIconType } from 'components/Common/ToastAlert/ToastAlert';
import { WHITE_LABELED_PRODUCT_NAMES, isInsuranceioBrand } from 'config';

/**
 * @brief Parse and return the response or throw in case of any errors.
 *
 * This function takes the response returned by a fetch API call, checks for
 * errors and throws the appropriate message which can be displayed as text.
 * @param {Response} response the response object from fetch API call
 * @return the RAW response text after parsing
 * @throws error message which can be displayed.
 */
export const responseErrorChecker = async (response) => {
  if (response.status === 500) {
    throw 'Internal Server Error';
  }

  const responseText = await response.text();
  const parsedResponseText = safeJSONParse(responseText, null);
  let message = '';
  // handle delete requests which don't return a response
  if (response.status === 204) {
    return responseText;
  }

  if (response.status === 401 && parsedResponseText?.errors?.code === ERR_DEVISE_SIGNATURE_VERIFICATION_FAILED) {
    window.location.replace('/login?session_expired=1')
  }

  if (response.status === 504 && parsedResponseText?.errors?.code === CONSTANTS.ERR_REQUEST_TIMEOUT) {
    ToastAlert.fire({ icon: ToastAlertIconType.ERROR, title: 'Request timeout, please try again later.', timer: 5000 })
  }

  if (typeof JSON.parse(responseText).errors !== 'undefined') {
    if (typeof JSON.parse(responseText).errors.detail !== 'undefined') {
      message = Object.keys(JSON.parse(responseText).errors.detail)[0];
      message = `${message
        } ${JSON.parse(responseText).errors.detail[message].join(',')}`;
      throw message;
    } else if (typeof JSON.parse(responseText).errors.title !== 'undefined') {
      throw JSON.parse(responseText).errors.title;
    } else if (typeof JSON.parse(responseText).errors.code !== 'undefined') {
      throw CONSTANTS.errorMessages[JSON.parse(responseText).errors.code] || 'Something went wrong. Please try again later... ';
    }
  }

  return responseText;
};

/**
 * Generic helper function to extract error message and show toast alert
 * @param {*} error - Error object or string from backend
 * @param {string} defaultMessage - Default message to show if error message is not found
 */
export const showErrorToast = (error, defaultMessage = GENERIC_ERROR_MESSAGE) => {
  let errorMessage = typeof error === 'string'
    ? error
    : (error?.errors?.title || error?.message || defaultMessage);

  // Update error message when offline, otherwise keep existing error
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  errorMessage = isOffline ? 'Please check your internet connection and try again' : errorMessage;

  ToastAlert.fire({ icon: ToastAlertIconType.ERROR, title: errorMessage });
};

/**
 * @brief get the option for a selected value in a select box (dropdown)
 *
 * @param {array} options the options array (of objects, each with a "label" and a "value")
 * @param {string} value the value to search for
 * @return option found, undefined otherwise
 */
export const getOptionFromValueForSelectBox = (options, value) => options?.find((opt) => (opt?.value === value));
export const getOptionFromValueForSelectBoxWithLabel = (options, label) => options?.find((opt) => (opt?.label === label));

/**
 * @brief Parse a JSON string and check if the parsed object contains a label
 * 
 * @param {string} value The JSON string to parse
 * @return {string|null} The label if found, null otherwise
 */
export const getLabelFromParsedValue = (value) => {
  try {
    if (!value) return null;
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed[0]?.label || null;
    }
    return parsed?.label || null;
  } catch (e) {
    return null;
  }
};

/**
 * @brief filter out values in an array. Essentially a SET DIFFERENCE
 *
 * @param {Array} values array with all the values
 * @param {Array} values_to_remove array with all the values to remove
 * @return values - values_to_remove array
 */
export const filterOutArrayValues = (values, values_to_remove) => values.filter((v) => !values_to_remove.includes(v));

export const SUPPORTED_TARGET_CASE = {
  CAPITALIZE_EVERY_WORD: 1,
};

export const capitalizeWord = (word) => {
  if (word && typeof word === 'string') {
    return word.charAt(0).toUpperCase() + word.slice(1);
  }

  return '';
};

export const capitalizeFirstLetter = (str) => {
  if (str && typeof str === 'string' && str.length > 0) {
    return str.trim().charAt(0).toUpperCase() + str.slice(1);
  }
  return '';
};
export const allowOnlyNumberandDot = (value) => {
  if (!value) {
    return ''
  }
  return value?.replace(CONSTANTS.numberOnlyRegex, ''); ///[^0-9.]/g, ''
}

/**
 * @brief this function takes a string in "snake_case" or "camelCase", and returns a new string in the target case.
 *
 * @see SUPPORTED_TARGET_CASE for supported target cases
 * @param {String} sourceString
 * @param {SUPPORTED_TARGET_CASE} targetCase
 * @returns string in the supported target case
 */
export const changeCase = (sourceString, targetCase) => {
  const intermediateResultArray = sourceString
    ?.replace(/_/g, ' ') // add spaces for underscores [abc_xyz => abc xyz]
    ?.replace(/([A-Z])/g, ' $1') // add spaces for uppercase letters in middle of word ["camelCase" => " camel Case"]
    ?.replace(/ +/, ' ') // multiple spaces to single space "abc   xyz" => "abc xyz"
    ?.trim()
    ?.split(' ');

  switch (targetCase) {
    case SUPPORTED_TARGET_CASE.CAPITALIZE_EVERY_WORD:
      return intermediateResultArray?.map((val) => capitalizeWord(val))?.join(' ');
  }
};

/**
 * @todo Check if this function is still required since we're just returning the input
 * @brief Get the IANA timzone string for timezone
 *
 * @param {String} timezoneAbbreviation timezone abbreviation (PT, ET...)
 * @return IANA timezone string
 */
export const getIANATimezoneStringForAbbreviation = (timezoneAbbreviation) => {
  switch (timezoneAbbreviation) {
    case 'America/Los_Angeles':
      return 'America/Los_Angeles';
    case 'America/New_York':
      return 'America/New_York';
    case 'America/Chicago':
      return 'America/Chicago';
    case 'America/Denver':
      return 'America/Denver';
    case 'America/Juneau':
      return 'America/Juneau';
    case 'Pacific/Honolulu':
      return 'Pacific/Honolulu';
    case 'Etc/UTC':
      return 'Etc/UTC';
  }
};

export const getCurrentBrandFromLocalStorage = () => {
  if (localStorage.getItem('current_brand')) {
    return JSON.parse(localStorage.getItem('current_brand'));
  }
  return null;
};

export const setCurrentBrandInLocalStorage = (brand) => {
  localStorage.setItem('current_brand', JSON.stringify({ id: brand.id, name: brand.name }));
};

/**
 * @brief Change start of week from monday to sunday
 *
 * In the UI, Monday is shown at the 0th index. However
 * for the backend, Sunday has to be on the 0th index.
 *
 * @param {number} dayZeroIndexedOnMonday
 */
export const getDayIdFromZeroIndexedMonday = (dayZeroIndexedOnMonday) => (dayZeroIndexedOnMonday + 8) % 7;

export function getRandomArbitrary(max) {
  let min = 0;
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const gethourToTimeConversion = (key_name) => {
  switch (key_name) {
    case 'h0':
      return '12 am';

    case 'h1':
      return '1 am';

    case 'h2':
      return '2 am';

    case 'h3':
      return '3 am';
    case 'h4':
      return '4 am';

    case 'h5':
      return '5 am';

    case 'h6':
      return '6 am';

    case 'h7':
      return '7 am';

    case 'h8':
      return '8 am';

    case 'h9':
      return '9 am';

    case 'h10':
      return '10 am';

    case 'h11':
      return '11 am';

    case 'h12':
      return '12 pm';

    case 'h13':
      return '1 pm';

    case 'h14':
      return '2 pm';

    case 'h15':
      return '3 pm';

    case 'h16':
      return '4 pm';

    case 'h17':
      return '5 pm';

    case 'h18':
      return '6 pm';

    case 'h19':
      return '7 pm';

    case 'h20':
      return '8 pm';

    case 'h21':
      return '9 pm';

    case 'h22':
      return '10 pm';

    case 'h23':
      return '11 pm';
    default: return '12 pm';
  }
};

const normalizeCacheValue = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizeCacheValue)
  }

  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((accumulator, key) => {
        accumulator[key] = normalizeCacheValue(value[key])
        return accumulator
      }, {})
  }

  return value
}

export const buildCampaignAnalysisCacheKey = (queryParams = {}) => {
  const { page_number, ...baseQueryParams } = queryParams || {}
  return JSON.stringify(normalizeCacheValue(baseQueryParams))
}

export const buildPaginationCacheKey = (queryParams = {}) => {
  const { page_number, ...baseQueryParams } = queryParams || {}
  return JSON.stringify(normalizeCacheValue(baseQueryParams))
}

export const buildAdminBillingEventsCacheKey = (queryParams = {}) => {
  const { page_number, appendResults, useCache, cacheKey, ...baseQueryParams } = queryParams || {}
  return JSON.stringify(normalizeCacheValue(baseQueryParams))
}

// to get the corresponding id of current day in respective timezone
export const getToDayInConst = (inTimeZone = REPORTING_TIMEZONE,) => {
  if (inTimeZone === TIMEZONE_NAMES.LOCAL_TIME.toLowerCase()) {
    inTimeZone = BROWSER_IANA_TIMEZONE
  }
  return new Date(new Date().toLocaleString('en-US', { timeZone: inTimeZone })).getDay();
}

export const getToday = (inTimeZone = REPORTING_TIMEZONE) => {
  const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  if (inTimeZone === TIMEZONE_NAMES.LOCAL_TIME.toLowerCase()) {
    inTimeZone = BROWSER_IANA_TIMEZONE
  }
  const d = getToDayInConst(inTimeZone)
  return weekday[d]
};


export const sfLog = (['staging', 'production'].includes(process.env.NODE_ENV)) ? () => { } : console.log;
export const sfTrace = (['staging', 'production'].includes(process.env.NODE_ENV)) ? () => { } : console.trace;

export const percIncrease = (a, b) => {
  // BAD HACK: Need to change the format through server response
  // TODO
  a = _.toInteger(a * 100);
  b = _.toInteger(b * 100);

  let percent;
  if (a !== 0) {
    percent = (b - a) / a * 100;
  } else {
    percent = b * 100;
  }
  return percent.toFixed(2);
};

export const percentage = (a, b) => {

  a = _.toInteger(a);
  b = _.toInteger(b);
  if (b === 0) {
    return 0;
  }
  let percent;
  percent = a / b * 100;
  return percent.toFixed(2);

}

export const compareIds = (a, b) => {
  if (a.id === undefined || a.id === null) {
    return -1; // `a` is considered smaller
  }
  if (b.id === undefined || b.id === null) {
    return 1; // `b` is considered smaller
  }
  return a.id - b.id; // normal comparison
}

export const getDatesInBetween = (startDate, stopDate) => {
  const dateArray = new Array();
  let currentDate = startDate;
  dateArray.push(startDate)
  while (addDays(currentDate, 1) <= stopDate) {
    currentDate = addDays(currentDate, 1);
    dateArray.push(new Date(currentDate));
  }
  return dateArray;
};

export const getWeeksInBetween = (startDate, stopDate) => {
  const dateArray = new Array();
  let currentDate = startDate;
  dateArray.push(startDate)
  while (addDays(currentDate, 7) <= stopDate) {
    currentDate = addDays(currentDate, 7);
    dateArray.push(new Date(currentDate));
  }
  return dateArray;
};

export const getMonthsInBetween = (startDate, stopDate, timezone) => {
  const dateArray = new Array();
  let currentDate = startDate;
  dateArray.push(startDate)
  while (addMonths(currentDate, 1) <= stopDate) {
    currentDate = addMonths(currentDate, 1);
    dateArray.push(getZonedStartOfMonth(currentDate, timezone));
  }
  return dateArray;
};

export const getHoursInBetween = (startDate, stopDate) => {
  const dateArray = new Array();
  let currentDate = startDate;
  dateArray.push(startDate)
  while (addHours(currentDate, 3) <= stopDate) {
    currentDate = addHours(currentDate, 3);
    dateArray.push(new Date(currentDate));
  }
  return dateArray;
};

export const getFilterModalLatestDateValue = (currentAccountTimeZone, defaultDate, dateRangeLabel, dateRangeValue) => {
  let modalStateDate = createOption(
    defaultDate,
    new Date(),
    getIANATimezoneStringForAbbreviation(currentAccountTimeZone),
  )
  if (dateRangeLabel) {
    modalStateDate = {
      label: DATE_RANGE_OPTIONS_DISPLAY[dateRangeLabel],
      value: JSON.parse(dateRangeValue || '{}'),
      original_label: dateRangeLabel,
      ianaTz: getIANATimezoneStringForAbbreviation(currentAccountTimeZone)
    }
  }
  return modalStateDate;
}

export const timeFormatterFromMilliseconds = (totalMillis) => {
  const hours = Math.floor(totalMillis / (60 * 60 * 1000));
  const minutes = Math.floor((totalMillis % (60 * 60 * 1000)) / (60 * 1000));
  const seconds = Math.floor((totalMillis % (60 * 1000)) / 1000);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

export const titleize = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * @brief Get CurrencyFormat with $ prefix, thousand separated and fixed decimal scale of 2
 *
 * @param {string|number} value to get the currecy format for
 * @returns a CurrencyFormat component prefixed with $ sign, thousand separated
 */
export const dollarFormatWithTwoDecimalPlaces = (value) => (
  <CurrencyFormat
    thousandSeparator
    displayType="text"
    prefix="$"
    value={(value === null) ? '0' : value}
    decimalScale={2}
    fixedDecimalScale
  />
);

export const dollarFormatWithRanges = (value) => {
  if (typeof value === 'number') {
    return dollarFormatWithTwoDecimalPlaces(value)
  } else if (typeof value === 'string') {
    if (value.match(CONSTANTS.DOLLAR_PATTERN) || value.match(CONSTANTS.RANGE_PATTERN_WITH_DOLLAR)) {
      return value
    } else {
      return dollarFormatWithTwoDecimalPlaces(value)
    }
  }
  return value;
}

/**
 * @brief Get CurrencyFormat with % suffix, thousand separated and fixed decimal scale of 2
 *
 * @param {string|number} value to get the currecy format for
 * @returns a CurrencyFormat thousand separated
 */
export const percentFormatWithTwoDecimalPlaces = (value) => (
  <CurrencyFormat
    displayType="text"
    value={(value === null) ? '0' : value}
    suffix="%"
    decimalScale={2}
    fixedDecimalScale
    thousandSeparator
  />
);

export function Sfdelimitter(value) {
  return (
    <CurrencyFormat
      displayType="text"
      decimalScale={2}
      value={(value === null) ? '0' : value}
      thousandSeparator
    />
  );
}

/**
 * @brief Get CurrencyFormat thousand separated and fixed decimal scale of 2
 *
 * @param {string|number} value to get the currecy format for
 * @param {object} prefix_suffix object with {prefix:string, suffix:string}
 * @returns a CurrencyFormat thousand separated
 */
export const commaFormatWithTwoDecimalPlaces = (value, { prefix, suffix } = {}) => (
  <CurrencyFormat
    displayType="text"
    value={(value === null) ? '0' : value}
    decimalScale={2}
    prefix={prefix || ''}
    suffix={suffix || ''}
    fixedDecimalScale
    thousandSeparator
  />
);

// Max limit to set campaign budget
export const maxBudgetAndUnitsLimit = (values, maxValue) => {
  const { formattedValue, floatValue } = values;
  return formattedValue === "" || floatValue <= maxValue;
}

/** sort an array of objects by a given key
 * @param {array} array of objects to be sorted
 * @param {string} key to sort by
 * @param {boolean} reverse if true, sort in reverse order
 * @returns {array} sorted array
 * */

export const sortByKey = (array, key) => array.sort((a, b) => {
  const x = a[key]; const y = b[key];
  return ((x < y) ? -1 : ((x > y) ? 1 : 0));
});

export const sortByCreationDate = (a, b) => {
  const keyA = new Date(a?.created_at);
  const keyB = new Date(b?.created_at);
  // Compare the 2 dates
  if (keyA < keyB) return -1;
  if (keyA > keyB) return 1;
  return 0;
};
export const sortByCreationDateDesc = (b, a) => {
  const keyA = new Date(a?.created_at);
  const keyB = new Date(b?.created_at);
  // Compare the 2 dates
  if (keyA < keyB) return -1;
  if (keyA > keyB) return 1;
  return 0;
};
export const sortStringAsFloat = (a, b, order, dataField, rowA, rowB) => {
  const a_as_float = parseFloat(a) || 0.0;
  const b_as_float = parseFloat(b) || 0.0;
  if (order === 'asc') {
    return a_as_float - b_as_float;
  }
  return b_as_float - a_as_float;
};

export const sortPixelsDefault = (a, b, order, dataField, rowA, rowB, name) => {

  if (a - b === 0) {          // if counts are equal
    const isRowARevenueLarger = rowA?.click_conversions_revenue - rowB?.click_conversions_revenue;
    const isRowANameLarger = name && rowA?.[name]?.localeCompare(rowB?.[name]);

    if (isRowARevenueLarger !== 0) {     // sort based on Revenue
      if (order === 'asc') {
        return isRowARevenueLarger;
      }
      return -1 * isRowARevenueLarger;
    }
    else if (isRowANameLarger) {            //if revenue is equal then sort based on names
      return isRowANameLarger;
    }
  }
  else if (order === 'asc') {
    return a - b;
  }
  return b - a;
};

// not used
export const sortCostColumn = (a, b, order, dataField, rowA, rowB, name) => {
  const a_as_float = parseFloat(a) || 0.0;
  const b_as_float = parseFloat(b) || 0.0;

  if (a - b === 0) {         // if costs are equal
    const isRowAImpressionsLarger = rowA?.click_analytic_impressions - rowB?.click_analytic_impressions;
    const isRowAClicksLarger = rowA?.click_analytic_clicks - rowB?.click_analytic_clicks;
    const isRowACtrLarger = rowA?.click_analytic_ctr - rowB?.click_analytic_ctr;
    const isRowANameLarger = name && rowA?.[name]?.localeCompare(rowB?.[name]);

    if (isRowAImpressionsLarger !== 0) {     // sort based on impressions
      if (order === 'asc') {
        return isRowAImpressionsLarger;
      }
      return -1 * isRowAImpressionsLarger;
    }
    else if (isRowAClicksLarger !== 0) {    //if impressions are equal then sort based on clicks
      if (order === 'asc') {
        return isRowAClicksLarger;
      }
      return -1 * isRowAClicksLarger;
    }
    else if (isRowACtrLarger !== 0) {       //if clicks are equal then sort based on ctr
      if (order === 'asc') {
        return isRowACtrLarger;
      }
      return -1 * isRowACtrLarger;
    }
    else if (isRowANameLarger) {            //if ctr is equal then sort based on names
      return isRowANameLarger;
    }
  }
  else if (order === 'asc') {
    return a - b;
  }
  return b - a;
};

/**
 * @brief this function is used to compare two rows and sort based on the order
 * sorts based on order passed if values are equal
 * 
 * @param {number} a the value of cell of first row
 * @param {number} b the value of cell of second row
 * @param {object} rowA the data of first row
 * @param {object} rowB the data of second row
 * @param {string} order 'asc' or 'desc'
 * @param {array} sortOrder an array of columns of type number for order preference when the data is equal
 * @param {array} lastColumn used only if sort order preference is string (normally used for chronological ordering)
 * @returns {number} '-1,0'= do not swap, '1' = swap
 */
export const sortOrderBased = (a, b, order, dataField, rowA, rowB, sortOrder, lastColumn) => {

  if (a - b === 0) {
    let i = 0
    while (i < sortOrder?.length) {
      a = rowA?.[sortOrder?.[i]]
      b = rowB?.[sortOrder?.[i]]
      if (a - b !== 0) {
        if (order === 'asc') {
          return a - b;
        }
        return b - a;
      }
      i++
    }
    return rowA?.[lastColumn]?.localeCompare(rowB?.[lastColumn])
  }
  else if (order === 'asc') {
    return a - b;
  }
  return b - a;
}

export const sortConnectedToColumn = (a, b, order, dataField, rowA, rowB) => {
  const a_as_int = parseInt(a) || 0;
  const b_as_int = parseInt(b) || 0;

  if (order === 'desc') {
    return (a_as_int < b_as_int) ? -1 : 1;
  }
  return (b_as_int > a_as_int) ? 1 : -1;
};

export const SfDollarStringFormatter = (value) => {
  if (value === undefined || value === null) {
    value = 0;
  }
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',

    // These options are needed to round to whole numbers if that's what you want.
    // minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    // maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });
  return formatter.format(value);
};

export const formatToUsdWithTwoDecimalPlaces = (value) => {
  if (value === undefined || value === null) {
    value = 0;
  }
  value = Math.floor(value * 100) / 100;
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return formatter.format(value);
}

export const SfDollarStringFormatterWithRanges = (value) => {
  if (value === undefined || value === null) {
    value = 0;
  }
  if (typeof value === 'number') {
    return SfDollarStringFormatter(value)
  } else if (typeof value === 'string') {
    if (value.match(CONSTANTS.DOLLAR_PATTERN) || value.match(CONSTANTS.RANGE_PATTERN_WITH_DOLLAR)) {
      return value
    } else {
      return SfDollarStringFormatter(value)
    }
  }
  return value;
}

export const SfPercentStringFormatter = (value) => `${new Intl.NumberFormat().format(value)}%`;
export const SfCommaStringFormatter = (value) => `${new Intl.NumberFormat().format(value)}`;
export const SfCommaStringTwoDecimalFormatter = (value) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const getYearListFromReceipts = (receipts, timezone) => {
  const years = [];
  receipts.forEach((receipt, index) => {
    const year = new Date(receipt.created_at).getFullYear();
    const startOfMonth = getZonedStartOfMonth(new Date(receipt.created_at), timezone)
    const startofThisMonth = getZonedStartOfMonth(new Date(), timezone)
    if (isBefore(startOfMonth, startofThisMonth) && years.indexOf(year) === -1) {
      years.push(year);
    }
  });
  return years;
};

export const getYearListFromInvoices = (invoices) => {
  return invoices.map(e => e.year).filter((v, i, a) => a.indexOf(v) === i)
};

export const getPastInvoices = (invoices) => {
  const todayInReportingTimezone = formatDatePST(new Date);
  return invoices.filter((e) => isBefore(parseISO(e.to_date), parseISO(todayInReportingTimezone)));
}

export const getDatePaidFromInvoiceReceipts = (receipts, invoice_id) => {
  const credit_receipts_for_invoice = receipts.filter(e => (e.receipt_type_id === 0
    && e.invoice_id.toString() === invoice_id.toString()))
  if (credit_receipts_for_invoice.length === 0) {
    return null
  }
  const latest_credit_receipt_for_invoice = credit_receipts_for_invoice?.
    reduce((a, b) => (a.created_at > b.created_at ? a : b), credit_receipts_for_invoice[0]?.created_at);
  return latest_credit_receipt_for_invoice.created_at
};

export const getMonthYearListFromReceipts = (receipts, timezone) => {
  const years = [];

  receipts.forEach((receipt, index) => {
    const startOfMonth = getZonedStartOfMonth(new Date(receipt.created_at), timezone)
    const startofThisMonth = getZonedStartOfMonth(new Date(), timezone)

    //Disabling check for monthly reports, show even current month
    //if (isBefore(startOfMonth, startofThisMonth)) {

    if (!years.some(((element) => element.year === startOfMonth.getFullYear() &&
      element.month === (startOfMonth.getMonth() + 1)))) {
      years.push({
        year: startOfMonth.getFullYear(),
        month: (startOfMonth.getMonth() + 1),
        created_at: receipt.created_at, brand_name: receipt.brand.name,
      });
    }
    //}
  });
  return years;
};

export const getUniqueMonthsListSinceAccountCreation = (account_creation_date, timezone, generate_report_with_current_month = false) => {
  const years = [];
  const startofThisMonth = getZonedStartOfMonth(new Date(), timezone)
  const startofAccountCreationMonth = getZonedStartOfMonth(new Date(account_creation_date), timezone)
  let startOfMonth = startofAccountCreationMonth
  while ((isSameMonth(startOfMonth, startofThisMonth) && generate_report_with_current_month) || isBefore(startOfMonth, startofThisMonth)) {
    if (!years.some(((element) => element.year === startOfMonth.getFullYear() &&

      element.month === (startOfMonth.getMonth() + 1)))) {
      years.push({
        year: startOfMonth.getFullYear(),
        month: (startOfMonth.getMonth() + 1),
        created_at: startOfMonth,
      });
    }
    startOfMonth = addMonths(startOfMonth, 1)
  }
  return years;
};

export const getUniqueYearsListSinceAccountCreation = (account_creation_date, timezone) => {
  const years = [];
  const startofThisMonth = getZonedStartOfMonth(new Date(), timezone)
  const startofAccountCreationMonth = getZonedStartOfMonth(new Date(account_creation_date), timezone)
  let startOfMonth = startofAccountCreationMonth
  while (isBefore(startOfMonth, startofThisMonth)) {
    if (!years.some(((element) => element === startOfMonth.getFullYear()))) {
      years.push(startOfMonth.getFullYear());
    }
    startOfMonth = addMonths(startOfMonth, 1)
  }
  return years;
};

export const checkUrlFiltersPresent = (urlQueryParams) => {
  return urlQueryParams?.indexOf('?') > 0
}

export const getParsedFilters = (urlQueryParams) => {
  const prevQueryParams = urlQueryParams.split('filters=')[1];
  const decodedURI = decodeURIComponent(prevQueryParams ? prevQueryParams : DEFAULT_FILTERD_URL_PARAMS)
  return safeJSONParse(decodeURIComponent(decodedURI), {});
}

export const getLeadTypeFromMemo = (memo) => capitalizeWord(memo.split(' ')[0]);

export const getTotalDuesForMonth = (receipts, month, year, timezone) => {
  let total = 0;

  const startOfMonth = getZonedStartOfMonth(new Date(year, month - 1, 15), timezone)
  const endOfMonth = getZonedEndOfMonth(startOfMonth, timezone)
  //Include only record processing fee
  // between start and end of month and
  // and promo = 0
  receipts = receipts.filter(e => ((e.promo === 0 && e.receipt_type_id === 1) &&
    (isAfter(new Date(e.created_at), startOfMonth)
      && (isBefore(new Date(e.created_at), endOfMonth)))))

  total = receipts.reduce((last_val, element) => {
    return last_val + parseFloat(element.debit)
  }, 0)
  return total;
};

export const getDueDate = (month, year) => {
  const date = new Date(year, month, 0);
  return date.toISOString().split('T')[0];
};

export const getTotalAmountPaidForMonth = (receipts, month, year, timezone) => {
  let total = 0;
  const startOfMonth = getZonedStartOfMonth(new Date(year, month, 1), timezone)
  const endOfMonth = getZonedEndOfMonth(startOfMonth, timezone)
  //Filter only manual account deposit fee and the receipts
  // between start and end of month 
  receipts = receipts.filter(e => ((e.receipt_type_id === 0) &&
    (getZonedStartOfDay(new Date(e.created_at), timezone) >= startOfMonth)
    && (getZonedEndOfDay(new Date(e.created_at), timezone) <= endOfMonth)))

  total = receipts.reduce((last_val, element) => {
    return last_val + parseFloat(element.credit)
  }, 0)
  return total;
};

export const getDatePaid = (receipts, month, year, timezone) => {
  const startOfMonth = getZonedStartOfMonth(new Date(year, month, 1), timezone)
  const endOfMonth = getZonedEndOfMonth(startOfMonth, timezone)
  //Filter only record processing fee and the receipts
  // between start and end of month 
  receipts = receipts.filter(e => ((e.receipt_type_id === 0) &&
    (getZonedStartOfDay(new Date(e.created_at), timezone) >= startOfMonth)
    && (getZonedEndOfDay(new Date(e.created_at), timezone) <= endOfMonth)))

  return receipts.pop()?.created_at;
};

export const getTotalOutstandingForMonth = (receipts, month, year, timezone) => {

  const startOfMonth = getZonedStartOfMonth(new Date(year, month, 1), timezone)
  const endOfMonth = getZonedEndOfMonth(startOfMonth, timezone)
  //Filter only record processing fee and the receipts
  // between start and end of month 
  const receipts_debit = receipts.filter(e => ((e.receipt_type_id === 1 && e.promo === 0) &&
    (getZonedStartOfDay(new Date(e.created_at), timezone) >= startOfMonth)
    && (getZonedEndOfDay(new Date(e.created_at), timezone) <= endOfMonth)))

  //Filter only manual account deposit and promo credits
  // between start and end of month 

  const receipts_credit = receipts.filter(e => ((e.receipt_type_id === 0)
    && (getZonedStartOfDay(new Date(e.created_at), timezone) >= startOfMonth)
    && (getZonedEndOfDay(new Date(e.created_at), timezone) <= endOfMonth)))

  const total_debits = receipts_debit.reduce((last_val, element) => {
    return last_val + parseFloat(element.debit)
  }, 0);
  const total_credits = receipts_credit.reduce((last_val, element) => {
    return last_val + parseFloat(element.credit)
  }, 0);

  return (total_debits - total_credits);
};

export const handleSessionTimeOut = (error, history) => {
  if (error && error === CONSTANTS.errorMessages.ERR_DEVISE_SIGNATURE_HAS_EXPIRED) {
    localStorage.removeItem('authUser');

    // HACK - this is a hack to get the user to log in again
    // TODO - fix this using history.push later
    window.location = '/login?session_expired=1';

    // history.push('/login');
  }
};

export const showTimeOutMessage = (session_expired_param, errorList) => {
  if (session_expired_param) {
    return (
      <Alert color="danger">
        {CONSTANTS.errorMessages.ERR_DEVISE_SIGNATURE_HAS_EXPIRED}
      </Alert>
    );
  }

  if (errorList.find((error) => error === CONSTANTS.errorMessages.ERR_DEVISE_SIGNATURE_HAS_EXPIRED)) {
    return (
      <Alert color="danger">
        {CONSTANTS.errorMessages.ERR_DEVISE_SIGNATURE_HAS_EXPIRED}
      </Alert>
    );
  }
  return null;
};

// https://medium.com/@botfather/react-loading-chunk-failed-error-88d0bb75b406
export default function componentLoader(lazyComponent, attemptsLeft) {
  return new Promise((resolve, reject) => {
    lazyComponent()
      .then(resolve)
      .catch((error) => {
        // let us retry after 1500 ms
        setTimeout(() => {
          if (attemptsLeft === 1) {
            reject(error);
            return;
          }
          componentLoader(lazyComponent, attemptsLeft - 1).then(resolve, reject);
        }, 1500);
      });
  });
}

export const getHtmlSnippetByType = (row, htmlSnippetType) => {
  let html_snippet_data = "";

  if (htmlSnippetType === HTML_SNIPPET_TYPE.IFRAME) {
    html_snippet_data = `<iframe src="${AppConfiguration.BACKEND_URL + row?.ep_iframe}" height="1" width="1" frameborder="0"></iframe>`;
  }
  if (htmlSnippetType === HTML_SNIPPET_TYPE.IMAGE) {
    html_snippet_data = `<img src="${AppConfiguration.BACKEND_URL + row?.ep_image}" height="1" width="1" border="0" />`;
  }

  try {
    return html_snippet_data;
  } catch (error) {
    return "Not Set"
  } {

  }
}

export const AppContext = React.createContext('light');

const URLS_FOR_PERMISSIONS = {
  CHANGE_HISTORY: "history",
  CALLS: "calls",
  EMAIL_ACTIVITY: "email-activity",
  MANAGEMENT_FOR_USERS: "users",
  MANAGEMENT_FOR_ROLES: "roles",
  CAMPAIGNS: {
    pathname: "campaigns",
    hash_edit: "edit",
    hash_create: "create",
    hash_source_setting: "call-lead-source-setting",
    hash_profiles: "profiles",
    hash_adgroups: "adgroups",
  },
  BILLINGS: {
    pathname: "billings",
    hash: "#settings",
  },
  SCHEDULED_REPORTS: {
    pathname: "analytics",
    hash: "scheduled_reports"
  },
  BRAND_SETTINGS: {
    pathname: "settings",
    hash: "#settings-brands",
    hash_conversion_pixels: "#settings-pixels",
  },
  CREATIVES: {
    pathname: "creatives",
    hash_edit: "edit",
    hash_create: "create",
  },
}

const findRolePermission = ((rolePermissions, rolePermissionsKey) => rolePermissions.find(singe_obj => singe_obj.ui_key === rolePermissionsKey));

/**
 * @param {object} object 
 * @param {key} key 
 * @returns value of key from object
 */
export const getValueFromObject = ((object, key) => object[key]);

export const isNotAuthorized = (url, rolePermissions, current_account, isPlatformSettingFeatures) => {

  if (url.pathname === "") { return true; }  // blank url should not give any access 

  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.CHANGE_HISTORY && findRolePermission(rolePermissions, UI_PERMISSION_KEY.VIEW_CHANGE_HISTORY)?.isActive === false) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.BILLINGS.pathname && findRolePermission(rolePermissions, UI_PERMISSION_KEY.VIEW_BILLING)?.isActive === false) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.CREATIVES.pathname && findRolePermission(rolePermissions, UI_PERMISSION_KEY.MANAGE_CREATIVES)?.isActive === false) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.MANAGEMENT_FOR_USERS && findRolePermission(rolePermissions, UI_PERMISSION_KEY.MANAGE_USER_MANAGEMENT)?.isActive === false) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.MANAGEMENT_FOR_ROLES && findRolePermission(rolePermissions, UI_PERMISSION_KEY.MANAGE_USER_MANAGEMENT)?.isActive === false) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.CAMPAIGNS.pathname && findRolePermission(rolePermissions, UI_PERMISSION_KEY.VIEW_CAMPAIGNS)?.isActive === false) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.SCHEDULED_REPORTS.pathname && url.pathname.split("/")[2] === URLS_FOR_PERMISSIONS.SCHEDULED_REPORTS.hash && findRolePermission(rolePermissions, UI_PERMISSION_KEY.MANAGE_SCHEDULED_REPORTS)?.isActive === false) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.BRAND_SETTINGS.pathname && url.hash === URLS_FOR_PERMISSIONS.BRAND_SETTINGS.hash && findRolePermission(rolePermissions, UI_PERMISSION_KEY.MANAGE_BRANDS)?.isActive === false) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.BILLINGS.pathname && url.hash === URLS_FOR_PERMISSIONS.BILLINGS.hash && findRolePermission(rolePermissions, UI_PERMISSION_KEY.MANAGE_BILLING)?.isActive === false) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.CAMPAIGNS.pathname && url.pathname.split("/")[2] === URLS_FOR_PERMISSIONS.CAMPAIGNS.hash_create && findRolePermission(rolePermissions, UI_PERMISSION_KEY.MANAGE_CAMPAIGNS)?.isActive === false) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.CAMPAIGNS.pathname && url.pathname.split("/")[3] === URLS_FOR_PERMISSIONS.CAMPAIGNS.hash_edit && findRolePermission(rolePermissions, UI_PERMISSION_KEY.MANAGE_CAMPAIGNS)?.isActive === false) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.CALLS && getValueFromObject(current_account, NAME_OF_CURRENT_ACCOUNT_COLUMN.CALL_DETAILS_ENABLED) !== true) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.EMAIL_ACTIVITY && findRolePermission(rolePermissions, UI_PERMISSION_KEY.EMAIL_ACTIVITY)?.isActive === false) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.CREATIVES.pathname && getValueFromObject(isPlatformSettingFeatures, PLATFORM_SETTINGS_FEATURES.IS_CLICKS_PRODUCT_ENABLED) !== true) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.CAMPAIGNS.pathname && url.pathname.split("/")[3] === URLS_FOR_PERMISSIONS.CAMPAIGNS.hash_source_setting && getValueFromObject(isPlatformSettingFeatures, PLATFORM_SETTINGS_FEATURES.IS_SOURCE_SETTINGS_ENABLED) !== true) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.BRAND_SETTINGS.pathname && url.hash === URLS_FOR_PERMISSIONS.BRAND_SETTINGS.hash_conversion_pixels && getValueFromObject(isPlatformSettingFeatures, PLATFORM_SETTINGS_FEATURES.IS_PIXEL_CONVERSION_SETTINGS_ENABLED) !== true) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.CAMPAIGNS.pathname && url.pathname.split("/")[3] === URLS_FOR_PERMISSIONS.CAMPAIGNS.hash_profiles && getValueFromObject(isPlatformSettingFeatures, PLATFORM_SETTINGS_FEATURES.IS_PROFILES_ENABLED) !== true) {
    return true;
  }
  if (url.pathname.split("/")[1] === URLS_FOR_PERMISSIONS.CAMPAIGNS.pathname && url.pathname.split("/")[3] === URLS_FOR_PERMISSIONS.CAMPAIGNS.hash_adgroups && getValueFromObject(isPlatformSettingFeatures, PLATFORM_SETTINGS_FEATURES.IS_PROFILES_ENABLED) !== true) {
    return true;
  }

  return false;
}

export const URLS_FOR_ADMIN_PERMISSIONS = {
  CLIENT_USERS: 'client-users',
  ADMIN_DASHBOARD: 'dashboard',
  FEATURE_FLAGS: 'feature-flags',
  ADMIN_USER_ROLES: 'admin_user_roles',
  ADMIN_INTERNAL_USER: 'admin_internal_user',
  ADMIN_CREATE_USER_ROLE: 'admin_create_user_role',
  ADMIN_CHANGE_HISTORY: 'admin_change_history',
  MANAGE_ACCOUNTING: 'accounting',
  MANAGE_NOTIFICATION_TEMPLATES: 'admin_notification_templates',
  ADMIN_EMAIL_ACTIVITY: "email-activity",
  ADMIN_USER_ACTIVITY: "user_activity",
  ADMIN_INSURANCE_CARRIERS: "insurance_carriers",
  ADMIN_REFUND_REASONS: "refund_reasons",
  ADMIN_WHITELISTED_BRANDS: "whitelisted_brands",
  ADMIN_SF_FILTERS: "sf_filters",
  ADMIN_FILTER_PACKAGES: "filter_packages",
  DEV_DASHBOARD: "dev-dashboard",
  DMS_DASHBOARD: "dms-dashboard",
  ADMIN_PROFILE: "admin_profile"
}

export const isAdminAuthorized = (pathname, adminRolePermissions) => {

  const urlSegments = pathname.split('/');
  const insurance_io_user_token = bsh_getInsuranceIoUserDetails()?.token;
  const admin_user_token = bsh_getAdminUserDetails()?.token;

  //If url is among one of admin/sf_filters/calls or admin/sf_filters/leads or admin/sf_filters/clicks then check for manage sf_filters permission
  if (
    urlSegments.length >= 4 &&
    urlSegments[1] === 'admin' &&
    [NAME_OF_PRODUCT_TYPES.CLICKS, NAME_OF_PRODUCT_TYPES.LEADS, NAME_OF_PRODUCT_TYPES.CALLS].includes(urlSegments[2]) &&
    urlSegments[3] === URLS_FOR_ADMIN_PERMISSIONS.ADMIN_SF_FILTERS
  ) {
    return !!_.intersection(adminRolePermissions, [
      ADMIN_USER_PERMISSIONS.MANAGE_SF_FILTERS,
    ]).length;
  }

  //If url is among one of admin/sf_filters/calls or admin/sf_filters/leads or admin/sf_filters/clicks then check for manage filter packages permission
  if (
    urlSegments.length >= 4 &&
    urlSegments[1] === 'admin' &&
    [NAME_OF_PRODUCT_TYPES.CLICKS, NAME_OF_PRODUCT_TYPES.LEADS, NAME_OF_PRODUCT_TYPES.CALLS].includes(urlSegments[2]) &&
    urlSegments[3] === URLS_FOR_ADMIN_PERMISSIONS.ADMIN_FILTER_PACKAGES
  ) {
    return !!_.intersection(adminRolePermissions, [
      ADMIN_USER_PERMISSIONS.MANAGE_FILTER_PACKAGES,
    ]).length;
  }

  switch (pathname.split("/")[1]) {
    case URLS_FOR_ADMIN_PERMISSIONS.CLIENT_USERS:
      return !!_.intersection(adminRolePermissions, [
        ADMIN_USER_PERMISSIONS.MANAGE_CLIENT_USERS,
        ADMIN_USER_PERMISSIONS.MANAGE_ASSIGNED_CLIENT_USERS,
        ADMIN_USER_PERMISSIONS.VIEW_CLIENT_USERS,
        ADMIN_USER_PERMISSIONS.VIEW_ASSIGNED_CLIENT_USERS
      ]).length
    case URLS_FOR_ADMIN_PERMISSIONS.ADMIN_DASHBOARD:
      return !!_.intersection(adminRolePermissions, [
        ADMIN_USER_PERMISSIONS.VIEW_ADMIN_DASHBOARD
      ]).length
    case URLS_FOR_ADMIN_PERMISSIONS.ADMIN_INTERNAL_USER:
      return !!_.intersection(adminRolePermissions, [
        ADMIN_USER_PERMISSIONS.MANAGE_ADMIN_USERS,
        ADMIN_USER_PERMISSIONS.MANAGE_ASSIGNED_ADMIN_USERS,
        ADMIN_USER_PERMISSIONS.VIEW_ADMIN_USERS,
        ADMIN_USER_PERMISSIONS.VIEW_ASSIGNED_ADMIN_USERS
      ]).length

    case URLS_FOR_ADMIN_PERMISSIONS.ADMIN_USER_ROLES:
      return !!_.intersection(adminRolePermissions, [
        ADMIN_USER_PERMISSIONS.MANAGE_ADMIN_ROLES
      ]).length

    case URLS_FOR_ADMIN_PERMISSIONS.ADMIN_SF_FILTERS:
      return !!_.intersection(adminRolePermissions, [
        ADMIN_USER_PERMISSIONS.MANAGE_SF_FILTERS
      ]).length

    case URLS_FOR_ADMIN_PERMISSIONS.FEATURE_FLAGS:
      return !!_.intersection(adminRolePermissions, [
        ADMIN_USER_PERMISSIONS.MANAGE_FEATURE_FLAGS
      ]).length

    case URLS_FOR_ADMIN_PERMISSIONS.MANAGE_ACCOUNTING:
      return !!_.intersection(adminRolePermissions, [
        ADMIN_USER_PERMISSIONS.MANAGE_ACCOUNTING
      ]).length

    case URLS_FOR_ADMIN_PERMISSIONS.ADMIN_CREATE_USER_ROLE:
      return !!_.intersection(adminRolePermissions, [
        ADMIN_USER_PERMISSIONS.MANAGE_ADMIN_ROLES
      ]).length

    case URLS_FOR_ADMIN_PERMISSIONS.MANAGE_NOTIFICATION_TEMPLATES:
      return !!_.intersection(adminRolePermissions, [
        ADMIN_USER_PERMISSIONS.MANAGE_NOTIFICATION_TEMPLATES
      ]).length

    case URLS_FOR_ADMIN_PERMISSIONS.ADMIN_EMAIL_ACTIVITY:
      return !!_.intersection(adminRolePermissions, [
        ADMIN_USER_PERMISSIONS.VIEW_EMAIL_ACTIVITY
      ]).length

    case URLS_FOR_ADMIN_PERMISSIONS.ADMIN_USER_ACTIVITY:
      return !!_.intersection(adminRolePermissions, [
        ADMIN_USER_PERMISSIONS.VIEW_USER_ACTIVITY
      ]).length

    case URLS_FOR_ADMIN_PERMISSIONS.ADMIN_CHANGE_HISTORY:
      return !!_.intersection(adminRolePermissions, [
        ADMIN_USER_PERMISSIONS.VIEW_CHANGE_HISTORY
      ]).length

    case URLS_FOR_ADMIN_PERMISSIONS.ADMIN_INSURANCE_CARRIERS:
      return !!_.intersection(adminRolePermissions, [
        ADMIN_USER_PERMISSIONS.MANAGE_INSURANCE_CARRIERS
      ]).length

    case URLS_FOR_ADMIN_PERMISSIONS.ADMIN_REFUND_REASONS:
      return !!_.intersection(adminRolePermissions, [
        ADMIN_USER_PERMISSIONS.MANAGE_REFUND_REASONS
      ]).length
    case URLS_FOR_ADMIN_PERMISSIONS.DEV_DASHBOARD:
      return ((insurance_io_user_token && (admin_user_token !== insurance_io_user_token)) ||
        !!_.intersection(adminRolePermissions, [
          ADMIN_USER_PERMISSIONS.VIEW_DEV_DASHBOARD
        ]).length)
    case URLS_FOR_ADMIN_PERMISSIONS.DMS_DASHBOARD:
      return isInsuranceioBrand
    default:
      return true
  }
}

export const prependHTTPStolink = (link) => {
  const prefix_https = 'https://';
  const prefix_http = 'http://';
  if ((link.substr(0, prefix_http.length) !== prefix_http) &&
    (link.substr(0, prefix_https.length) !== prefix_https)) {
    return (prefix_https + link);
  }
  return link
}

export const subdomainApplications = (domain_apps) => {
  let main = domain_apps.find((item) => item.main);
  if (!main) {
    throw new Error('Must set main flag to true on at least one subdomain app');
  }

  return function getComponent() {
    const parts = window.location.hostname.split('.').filter(part => part !== 'www');

    const is_admin = !!parts.find((part) => part.includes(AppConfiguration.REACT_ADMIN_HOST_SUBDOMAIN));
    const app = domain_apps.find((item) => (!item.main == is_admin)).application;
    return { app, is_admin };
  }
}


export const getFormattedLeadPriceByName = (lead_type_name, is_shared, lead_prices, is_profiles_enabled, filter_packages) => {
  const campaign_info = {
    product_type: NAME_OF_PRODUCT_TYPES.LEADS,
    lead_type: lead_type_name,
    shared_lead_type: is_shared ? true : false
  }
  const filterPackages = getFilterPackagesByProductAndLeadType(filter_packages, is_profiles_enabled, campaign_info);
  const lead_type = getLeadTypeByName(lead_type_name)
  const ele = lead_prices.find(e => e.shared === is_shared && e.lead_type_id === lead_type.id)
  if (filterPackages?.length > 0) {
    const filterPackagePrices = filterPackages?.map((filterPackage) => (parseFloat(filterPackage?.price)))
    return dollarFormatWithTwoDecimalPlaces(!!ele?.price ? _.min([...filterPackagePrices, ele?.price]) : _.min(filterPackagePrices))
  }
  return !ele?.price == false ? dollarFormatWithTwoDecimalPlaces(ele?.price) : `N/A`
}

export const getFormattedCallPriceByName = (lead_type_name, call_prices, is_profiles_enabled, filter_packages, inbound_only) => {
  const campaign_info = {
    product_type: NAME_OF_PRODUCT_TYPES.CALLS,
    lead_type: lead_type_name,
    shared_lead_type: true
  }
  const filterPackages = getFilterPackagesByProductAndLeadType(filter_packages, is_profiles_enabled, campaign_info);
  const lead_type = getLeadTypeByName(lead_type_name)
  const ele = call_prices.find(e => e.lead_type_id === lead_type.id && (!!e?.inbound_only) === inbound_only)
  if (filterPackages?.length > 0) {
    const filterPackagePrices = filterPackages?.map((filterPackage) => (parseFloat(filterPackage?.price)))
    return dollarFormatWithTwoDecimalPlaces(!!ele?.price ? _.min([...filterPackagePrices, ele?.price]) : _.min(filterPackagePrices))
  }
  return !ele?.price == false ? dollarFormatWithTwoDecimalPlaces(ele?.price) : `N/A`
}

export const getIndividualFromString = (string) => {
  return string.split(/\s+/).filter(str => str.length);
}

export const IsMonthlyBudgetFeatureEnabled = (account, product_type) => {
  if (!account.enterprise && ((product_type.name === NAME_OF_PRODUCT_TYPES.LEADS) || product_type.name === NAME_OF_PRODUCT_TYPES.CALLS)) {
    return false;
  }
  return true;
}

export const IsModifierFeatureEnabled = (account, product_type, is_lead_marketplace_campaign = false) => {
  if (!product_type) {
    return false
  }

  // Modifiers are available to enterprise accounts and Lead Marketplace campaigns.
  if (account.enterprise || is_lead_marketplace_campaign) {
    return true
  }

  // Modifiers are not available to retail accounts for Leads and Calls campaigns.
  return ![NAME_OF_PRODUCT_TYPES.LEADS, NAME_OF_PRODUCT_TYPES.CALLS].includes(product_type)
}

export const IsAdCreativeFeatureEnabled = (product_type) => {
  if (!product_type == true) {
    return false
  }
  return product_type === NAME_OF_PRODUCT_TYPES.CLICKS || product_type === NAME_OF_PRODUCT_TYPES.QUOTE_FUNNELS;
}

export const IsSourceSettingFeatureEnabled = (product_type) => {
  if (!product_type == true) {
    return false
  }
  if (((product_type.name === NAME_OF_PRODUCT_TYPES.LEADS) || product_type.name === NAME_OF_PRODUCT_TYPES.CALLS)) {
    return false;
  }
  return true;
}


export const AreMultipleFilterGroupsAllowed = (account, product_type_name, current_filter_length) => {
  if (!account.enterprise && ((product_type_name === NAME_OF_PRODUCT_TYPES.LEADS) || product_type_name === NAME_OF_PRODUCT_TYPES.CALLS)) {

    return current_filter_length === 1 ? false : true;
  }
  return true;
}

export const getScheduleMaxVal = (account, product_type_name) => {
  if (!account.enterprise && ((product_type_name === NAME_OF_PRODUCT_TYPES.LEADS) || product_type_name === NAME_OF_PRODUCT_TYPES.CALLS)) {
    return 100;
  }
  return 300;
}


export const IsAccountRetailAndProductTypeIsLeadsOrCalls = (account, product_type_name) => {
  if (!account.enterprise && ((product_type_name === NAME_OF_PRODUCT_TYPES.LEADS) || product_type_name === NAME_OF_PRODUCT_TYPES.CALLS)) {
    return true;
  }
  return false;

}

export const formatToISO = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export const getQueryParamsForDateRange = (time_label, ianaTz) => {
  const opt = createOption(time_label, new Date(), ianaTz);
  return {
    start_date: new Date(opt.value.startDate).toISOString(),
    end_date: new Date(opt.value.endDate).toISOString()
  };
}

export const volumeForecastCalc = (filterVolumeData, groupIndex) => {
  if (!filterVolumeData?.all_filtered_users) {
    return false;
  }
  const filter_group_index = groupIndex || 0;
  const all_filtered_users_by_index = filterVolumeData?.all_filtered_users[filter_group_index]
  const total_users = filterVolumeData?.all_total_users[filter_group_index] || 1

  const result = 100 * (all_filtered_users_by_index / total_users);
  return [Math.round(result), all_filtered_users_by_index];
}


export const openLinkInNewTab = (url) => {
  const win = window.open(url, '_blank');
  win.focus();
}

export const getCurrentAdminUserId = () => {
  let token = bsh_getAdminUserDetails().token;
  let decodedToken = jwt_decode(token.split(" ")[1]);
  return decodedToken.sub
}

export const CellFormat = ({ text }) => (
  <div className="p-1">
    <span>{text}</span>
  </div>
)

export const optionsNum1ToNum2 = (num1, num2) => {
  let opt = []
  for (let i = num1; i <= num2; i++) {
    opt.push({ label: `${i}`, value: i })
  }
  return opt;
}

export const optionsNum1ToNum2Seconds = (num1, num2) => {
  let opt = []
  for (let i = num1; i <= num2; i++) {
    opt.push({ label: `${i} seconds`, value: i })
  }
  return opt;
}

export const sortById = (arr) => {
  return arr.sort((a, b) => a.id - b.id)
}


export const generateFormattedCodeForMacro = (macro, formatter) => {
  switch (formatter) {
    case 'Date(DD/MM/YYYY)':
      return `{{ ${macro} | date: "%d/%m/%Y" }}`
    case 'Date(MM/DD/YYYY)':
      return `{{ ${macro} | date: "%m/%d/%Y" }}`
    case 'Date(MM/DD/YY)':
      return `{{ ${macro} | date: "%m/%d/%y" }}`
    case 'Date(YYYY/MM/DD)':
      return `{{ ${macro} | date: "%Y/%m/%d" }}`
    case 'Date(YYYY)':
      return `{{ ${macro} | date: "%Y" }}`
    case 'Date(YYYY-MM)':
      return `{{ ${macro} | date: "%Y-%m" }}`
    case 'Date(YYYY-MM-DD)':
      return `{{ ${macro} | date: "%Y-%m-%d" }}`
    case 'Date(YYYY-MM-DD HH:MM:SS)':
      return `{{ ${macro} | date: "%Y-%m-%d %H:%M:%S" }}`
    case 'Date(YYYY-MM-DD HH:MM:SS TZ)':
      return `{{ ${macro} | date: "%Y-%m-%d %H:%M:%S %Z" }}`
    case 'Date(YYYY-MM-DD HH:MM:SS TZ)':
      return `{{ ${macro} | date: "%Y-%m-%d %H:%M:%S %Z" }}`
    case 'Date(YYYY-MM-DD HH:MM:SS TZ)':
      return `{{ ${macro} | date: "%Y-%m-%d %H:%M:%S %Z" }}`
    case 'Date(YYYY-MM-DD HH:MM:SS TZ)':
      return `{{ ${macro} | date: "%Y-%m-%d %H:%M:%S %Z" }}`
    case 'Date(YYYY-MM-DD HH:MM:SS TZ)':
      return `{{ ${macro} | date: "%Y-%m-%d %H:%M:%S %Z" }}`
    case 'Date(MM-DD-YYYY)':
      return `{{ ${macro} | date: "%m-%d-%Y" }}`
    case 'Date(MM-DD-YYYY HH:MM:SS)':
      return `{{ ${macro} | date: "%m-%d-%Y %H:%M:%S" }}`
    case 'Date(MM-DD-YYYY HH:MM:SS TZ)':
      return `{{ ${macro} | date: "%m-%d-%Y %H:%M:%S %Z" }}`

    case 'Capitalize Text':
      return `{{ ${macro} | capitalize }}`
    case 'Lowercase Text':
      return `{{ ${macro} | downcase }}`
    case 'Uppercase Text':
      return `{{ ${macro} | upcase }}`
    case 'Remove Html Tags':
      return `{{ ${macro} | strip_html }}`
    case 'Remove Spaces':
      return `{{ ${macro} | strip }}`
  }
}

// Function to dynamically split header title into multiple lines
export const splitHeaderTitle = (columnTitle, splitHeader, preferSingleWordFirstRow) => {
  if (!splitHeader) {
    return (
      <>
        {columnTitle}
      </>
    );
  }

  // Split the title into words, handling a usecase to remove any empty strings from the words array.
  const words = columnTitle.split(' ').filter(word => word.trim() !== '')

  /// Special handling for exactly three words and special cases where sometimes we need one word in the first row i.e. preferSingleWordFirstRow is true
  if (words.length === 3) {
    if (preferSingleWordFirstRow) {
      const firstRow = words.slice(0, 1).join(' ');
      const secondRow = words.slice(1).join(' ');

      return (
        <>
          {firstRow}
          <br />
          {secondRow}
        </>
      );
    } else {
      // If preferSingleWordFirstRow is false
      const firstRow = words.slice(0, 2).join(' ');
      const secondRow = words.slice(2).join(' ');

      return (
        <>
          {firstRow}
          <br />
          {secondRow}
        </>
      );
    }
  }

  let wordsPerRow;
  if (words.length === 2) {
    // If exactly two words, put each on its own row
    wordsPerRow = 1;
  } else if (words.length > 3) {
    // If more than three  words, aim for two words per row
    wordsPerRow = 2;
  } else {
    // Default case for titles with less than two words (effectively, no splitting needed)
    wordsPerRow = words.length; // This handles the case of a single word or no words gracefully
  }

  // Function to chunk words into rows
  const chunkIntoRows = (words, wordsPerRow) => {
    const rows = [];
    for (let i = 0; i < words.length; i += wordsPerRow) {
      rows.push(words.slice(i, i + wordsPerRow).join(' '));
    }
    return rows;
  };

  // Create rows from the words
  const rows = chunkIntoRows(words, wordsPerRow);

  // Create formatted title JSX
  const formattedTitle = rows.map((row, index) => (
    <React.Fragment key={index}>
      {row}
      {index < rows.length - 1 && <br />}
    </React.Fragment>
  ));

  return formattedTitle;
};


//headerformatters start
/**
 * 
 * @param {object} _column 
 * @param {number} colIndex 
 * @param {object} param2 
 * @param {string} tooltipName 
 * @param {string} columnTitle 
 * @param {string} tooltipdescription 
 * @returns 
 */
export const tooltipForColumnHeader = (column, colIndex, { sortElement, filterElement }, tooltipName, columnTitle, tooltipdescription, splitHeader = false, preferSingleWordFirstRow = false) => {
  const formattedTitle = splitHeaderTitle(columnTitle, splitHeader, preferSingleWordFirstRow)

  return (
    <div className="d-flex align-items-end" style={column?.sort ? { cursor: 'pointer' } : {}}>
      <span className="me-1" >{formattedTitle}</span>
      <div className='d-flex align-items-center header-cell-parent'>
        <div>
          {sortElement}
        </div>
        <i id={`tooltip-${tooltipName}-${colIndex}`} className='ms-1 bx bx-info-circle text-black bx-xs'></i>
        <UncontrolledTooltip placement="top" target={`tooltip-${tooltipName}-${colIndex}`}>
          {tooltipdescription}
        </UncontrolledTooltip>
        {filterElement}
      </div>
    </div>
  );
}
export const forColumnHeader = (column, { sortElement, filterElement }, columnTitle, splitHeader = false, preferSingleWordFirstRow = false) => {
  const formattedTitle = splitHeaderTitle(columnTitle, splitHeader, preferSingleWordFirstRow)

  return (
    <div className="d-flex align-items-end" style={column?.sort ? { cursor: 'pointer' } : {}}>
      <span className="me-1" >{formattedTitle}</span>
      <div className='d-flex align-items-center header-cell-parent'>
        <div>
          {sortElement}
        </div>
        {filterElement}
      </div>
    </div>
  );
}

export const bothTooltipsForColumnHeader = (column, colIndex, { sortElement, filterElement }, tooltipName, columnTitle, tooltipdescription, isAdminOnlyColumn = bsh_getAdminUserDetails(), hidden, splitHeader = false, preferSingleWordFirstRow = false) => {
  const formattedTitle = splitHeaderTitle(columnTitle, splitHeader, preferSingleWordFirstRow)

  return (
    <div className="d-flex align-items-end" style={column?.sort ? { cursor: 'pointer' } : {}}>
      <span className="me-1" >{formattedTitle}</span>
      <div className='d-flex align-items-center header-cell-parent'>
        <div>
          {sortElement}
        </div>
        <i id={`tooltip-${tooltipName}-${colIndex}`} className='ms-1 bx bx-info-circle text-black bx-xs'></i>
        <UncontrolledTooltip placement="top" target={`tooltip-${tooltipName}-${colIndex}`}>
          {tooltipdescription}
        </UncontrolledTooltip>
        {isAdminOnlyColumn && !hidden && <AdminOnlyIcon idFor={tooltipName} tooltipText={ADMIN_ONLY_COLUMN} />}
        {filterElement}
      </div>
    </div>
  );
}

export const showOnlyAdminTooltipForColumnHeader = (column, _colIndex, { sortElement, filterElement }, tooltipName, columnTitle, isAdminOnlyColumn = bsh_getAdminUserDetails(), hidden, splitHeader = false, preferSingleWordFirstRow = false) => {
  const formattedTitle = splitHeaderTitle(columnTitle, splitHeader, preferSingleWordFirstRow)

  return (
    <div className="d-flex align-items-end" style={column?.sort ? { cursor: 'pointer' } : {}}>
      <span className="me-1" >{formattedTitle}</span>
      <div className='d-flex align-items-center header-cell-parent'>
        <div>
          {sortElement}
        </div>
        {isAdminOnlyColumn && !hidden && <AdminOnlyIcon idFor={tooltipName} tooltipText={ADMIN_ONLY_COLUMN} />}
        {filterElement}
      </div>
    </div>
  );
}

// Extended wrapper function for headerFormatters
export const forColumnHeaderWrapper = (column, colIndex, { sortElement, filterElement }) => {
  // Handle columns with admin icon tooltip and dynamic dataFields
  if (column.hasDynamicFields && column.hasAdminTooltip) {
    return bothTooltipsForColumnHeader(column, colIndex, { sortElement, filterElement }, column.dynamicTooltipID, column.text, column.tooltipDescription, column.isAdminOnly, column.dynamicHidden, column.splitHeader, column.preferSingleWordFirstRow);
  }
  // Handle columns with admin tooltip
  else if (column.hasAdminTooltip) {
    return showOnlyAdminTooltipForColumnHeader(column, colIndex, { sortElement, filterElement }, column.dataField, column.text, column.isAdminOnly, column.dynamicHidden, column.splitHeader, column.preferSingleWordFirstRow);
  }
  // Handle columns with only info icon tooltip
  else if (column.hasInfoTooltip) {
    return tooltipForColumnHeader(column, colIndex, { sortElement, filterElement }, column.dataField, column.text, column.tooltipDescription, column.splitHeader, column.preferSingleWordFirstRow);
  }
  // Handle columns with info icon tooltip and dynamic dataFields
  else if (column.hasDynamicFields) {
    return tooltipForColumnHeader(column, colIndex, { sortElement, filterElement }, column.dynamicTooltipID, column.text, column.tooltipDescription, column.splitHeader, column.preferSingleWordFirstRow);
  }
  // Handle columns with both admin and info icon tooltip
  else if (column.hasBothTooltips) {
    return bothTooltipsForColumnHeader(column, colIndex, { sortElement, filterElement }, column.dataField, column.text, column.tooltipDescription, column.isAdminOnly, column.dynamicHidden, column.splitHeader, column.preferSingleWordFirstRow);
  }
  else {
    return forColumnHeader(column, { sortElement, filterElement }, column.text, column.splitHeader, column.preferSingleWordFirstRow);
  }
};



//headerformatters end

export const uiStateForLeadIntegrationMacros = (sf_macros, lead_type_id) => {
  const filtered_sf_lead_integration_macros = sf_macros.filter(sflim => sflim.is_common || (sflim.sf_lead_integration_macro_lead_types.find(sf_lead_type => sf_lead_type.lead_type_id === lead_type_id) || false))
  const lead_integration_macros = []
  for (const sf_lead_integration_macro of filtered_sf_lead_integration_macros) {
    const data = {
      sf_lead_integration_macro_id: sf_lead_integration_macro.id,
      display_key: sf_lead_integration_macro.display_key,
      default_value: "",
      is_custom_mapping_active: false,
      is_enum: sf_lead_integration_macro.is_enum,
      key: sf_lead_integration_macro.key,
      lead_integration_macro_mappings: sf_lead_integration_macro?.is_enum ? sf_lead_integration_macro.enums.map(el => ({ "input_value": el, "output_value": "" })) : []
    }
    lead_integration_macros.push(data)
  }

  return lead_integration_macros;
}

/**
 * Merge the states from current macros and lead_integration_macros
 * @param {array} current_macros [{ id, key, display_key, lead_integration_macro_mappings, encoding_type, default_value, is_custom_mapping_active }]
 * @param {array} lead_integration_macros [{ key, display_key, lead_integration_macro_mappings, encoding_type, default_value, is_custom_mapping_active }]
 */
export const getLeadIntegrationMacrosForEdit = (current_macros, lead_integration_macros) => {
  const macros = (lead_integration_macros
    .map(li_macro => {
      const cur_li_macros = current_macros.filter(t => li_macro.sf_lead_integration_macro_id === t.sf_lead_integration_macro_id);
      if (!cur_li_macros.length) {
        return [li_macro];
      }

      return cur_li_macros.map(cur_li_macro => {
        const li_macro_mappings = li_macro.lead_integration_macro_mappings;
        const cur_li_macro_mappings = cur_li_macro.lead_integration_macro_mappings;
        const new_macro_mappings = li_macro_mappings.map(li_mm => ({
          ...li_mm,
          ...cur_li_macro_mappings.find(t => t.input_value === li_mm.input_value)
        }))

        return {
          ...cur_li_macro,
          lead_integration_macro_mappings: new_macro_mappings
        }
      })
    })
  )

  return _.flatten(macros)
}

export const addIsEnumToExistingMacros = (current_macros, sf_macros) => {
  const out = current_macros.map(cur_li_macro => ({
    ...cur_li_macro,
    is_enum: sf_macros.find(sf_macro => (sf_macro?.id === cur_li_macro?.sf_lead_integration_macro_id))?.is_enum || false
  }))

  return out;
}


export const parseJSON = (jsonString) => {
  try {
    const jsonObject = JSON.parse(jsonString);
    return jsonObject;
  } catch (error) {
    sfLog(error)
    return '';
  }
}

//undefined and Null Formmater
export const undefinedNullCellFormatter = (cell) => {
  switch (cell) {
    case undefined: return '--';
    case null: return 0;
    default: return cell.toLocaleString();
  }
}

export const pageContentPaddingTop = 84


export const getInitialProductType = (hasClicks, hasLeads, hasCalls, hasQuoteFunnels, isActiveForCallsAndLeads = false) => {
  if (isActiveForCallsAndLeads) {
    if (hasClicks) {
      return PRODUCT_TYPE_IDS.CLICKS
    } else if (hasLeads) {
      return PRODUCT_TYPE_IDS.LEADS
    } else if (hasCalls) {
      return PRODUCT_TYPE_IDS.CALLS
    } else if (hasQuoteFunnels) {
      return PRODUCT_TYPE_IDS.QUOTE_FUNNELS
    } else {
      return PRODUCT_TYPE_IDS.CLICKS
    }
  } else {
    return PRODUCT_TYPE_IDS.CLICKS
  }
}

/**
 * @brief get the JSON string of the value if value is not empty and not null else null
 * 
 * @param {any} value the multiselected options of the filters
 * @returns JSON string of the value if value is not empty and not null else null
 */
export const getJsonStringForFilters = (value) => {
  if (!_.isEmpty(value)) {
    return JSON.stringify(value);
  } else {
    return null;
  }
}

/** Filters local storage store the date object as string, this function will convert the string to date object
 * 
 * @param {object} option - the option object for the react-select from filters local storage
 * @returns option with value as date object
 */
export const changeOptionValueToDateObject = (option) => {
  if (!option) {
    return null
  } else if (option.label === DATE_RANGE_OPTIONS_DISPLAY.ALL_TIME) {
    return option
  } else if (option) {
    return {
      ...option,
      value: {
        ...option.value,
        startDate: new Date(option.value.startDate),
        endDate: new Date(option.value.endDate),
      },
    }
  } else {
    return option
  }
}

export const getCampaignLeadIntegrationConfigStr = (campaign_lead_integration) => {
  const campaign_lead_integration_config = '{}'
  try {
    JSON.parse(campaign_lead_integration?.config)
    return campaign_lead_integration?.config
  } catch (e) {
  }

  return campaign_lead_integration_config;
}

// name comparator for sorting null values to the end
export const nameComparator = (a, b) => {
  if (a === null && b === null) {
    return 0; // both are null, maintain current order
  }
  if (a === null && b !== null) {
    return 1; // push null values to the end
  }
  if (a !== null && b === null) {
    return -1; // keep non-null values before null values
  }
  // for non-null values, use the default string comparison
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

export const printNameOrEmail = (first_name, last_name, email, return_if_null = '--') => {
  return `${first_name || ''} ${last_name || ''} ${(first_name || last_name) ? '' : (email || return_if_null)}`
}

export const addDollarSignIfEmpty = (e, index = 0) => {
  if (e.target.value.length === index) {
    e.target.value = "$"
  }
}

export const handleKeyDown = event => {
  if (event.key === "Backspace") {
    addDollarSignIfEmpty(event, 0)
  }
}

export const getPrimaryButtonClass = () => {
  switch (AppConfiguration?.PRODUCT_NAME) {
    case 'Insuranceio':
      return 'btn-sfpink'
    case 'Ushaio':
      return 'btn-ushaprimary'
    case 'Insuranceagentsai':
      return 'btn-insuranceagentsprimary'
    case 'Goal':
      return 'btn-goalprimary'
    case 'Sfpro':
      return 'btn-sfpink'
    case 'Benepath':
      return 'btn-benepathprimary'
  }
  return 'btn-sfpink'
}

export const getBackgroundColorClass = () => {
  switch (AppConfiguration?.PRODUCT_NAME) {
    case 'Insuranceio':
      return '#2A3042'
    case 'Ushaio':
      return '#002250'
    case 'Insuranceagentsai':
      return '#062f4f'
    case 'Goal':
      return '#011020'
    case 'Sfpro':
      return '#2A3042'
    case 'Benepath':
      return '#31451e'
  }
}

export const getPrimaryTextClass = () => {
  switch (AppConfiguration?.PRODUCT_NAME) {
    case 'Insuranceio':
      return 'text-sfpink'
    case 'Ushaio':
      return 'text-ushaprimary'
    case 'Insuranceagentsai':
      return 'text-insuranceagentsprimary'
    case 'Goal':
      return 'text-goalprimary'
    case 'Sfpro':
      return 'text-sfpink'
    case 'Benepath':
      return 'text-benepathprimary'
  }
  return 'text-sfpink'
}

export const getAppropriateLeftSideBarTheme = (productName) => {
  switch (productName) {
    case 'Insuranceio':
      return leftSideBarThemeTypes.DARK;
    case 'Ushaio':
      return leftSideBarThemeTypes.USHA;
    case 'Insuranceagentsai':
      return leftSideBarThemeTypes.INSURANCEAGENTS;
    case 'Goal':
      return leftSideBarThemeTypes.GOAL;
    case 'Sfpro':
      return leftSideBarThemeTypes.DARK;
    case 'Benepath':
      return leftSideBarThemeTypes.BENEPATH;
    default:
      return leftSideBarThemeTypes.DARK;
  }
}

export const getAppropriateLoadingGif = (productName) => {
  switch (productName) {
    case 'Insuranceio':
      return <SfLoadingSpinner />;
    case 'Ushaio':
      return <LoadingSpinner />;
    case 'Insuranceagentsai':
      return <LoadingSpinner />;
    case 'Goal':
      return <LoadingSpinner />;
    case 'Sfpro':
      return <LoadingSpinner />;
    default:
      return <LoadingSpinner />;
  }
}

export const getMappedProductName = (product_name) => {
  switch (product_name) {
    case WHITE_LABELED_PRODUCT_NAMES.SFPRO:
    case WHITE_LABELED_PRODUCT_NAMES.INSURANCE_IO:
    case WHITE_LABELED_PRODUCT_NAMES.SMARTFINANCIALPRO:
      return 'SmartFinancial';
    default:
      return AppConfiguration.WEBSITE;
  }
}

export const nullHandlerCustomizeColumns = (value) => {
  if (value === null) {
    return true
  } else {
    return value
  }
}

/**
 * Get the lead refund status to show on the UI
 * 
 * @param {Object} lead_refund_status lead_refund_status string from backend
 * @param {Date} created_at Refund request created_at
 */
export const getLeadRedundStatus = (lead_refund_status, created_at) => {
  if (lead_refund_status && differenceInMinutes(new Date(), parseISO(created_at)) < LEAD_REFUND_DELAY_FOR_STATUS_IN_MINUTES)
    return 'Pending'
  else
    return lead_refund_status
}

export const safeJSONParse = (str, fallback) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return fallback;
  }
}

export const booleanFormatter = (cell, showTick = false) => {
  const iconClass = showTick
    ? (cell ? 'bx bx-check' : 'mdi mdi-close')
    : 'bx bxs-circle'

  return (
    <div className="d-inline-flex align-items-center justify-content-center">
      <i
        className={`${iconClass} font-size-1 ${cell ? 'text-success' : 'sfred'}`}
        style={showTick ? { fontSize: '1.2rem' } : {}}
      />
      <span className="ms-1 d-flex align-self-center fw-bold">
        {cell ? "Yes" : "No"}
      </span>
    </div>
  );
};

export const insuranceCarrierListFormatter = (list) => {
  if (!Array.isArray(list)) return null;
  return (
    <div>
      {list.map((item, index) => {
        const carrierName = item.carrier_name;
        const isConsented = item.consented;
        return (
          <div key={index} className="d-flex align-items-center mb-1">
            <i
              className={`${isConsented ? 'bx bx-check text-success' : 'mdi mdi-close text-danger'}`}
              style={{ marginRight: '8px', fontSize: '19px', lineHeight: '19px' }}
            />
            <span>{carrierName}</span>
          </div>
        );
      })}
    </div>
  );
};

export const sortInsuranceCarriers = (carriers) => {
  if (!Array.isArray(carriers)) return [];
  return [...carriers].sort((a, b) => a.consented ? -1 : 1);
};

export const getFilteredQuestions = (q_gr, answers) => {
  return q_gr.questions.filter(q => {
    if (!q.depends_on) {
      return true; // No dependency, include the question
    }
    const depends_on_question_obj = answers.find(ans => ans.field_name == q.depends_on);
    const depends_on_question_value = depends_on_question_obj?.value
    const parsed_depends_on_value = parseFloat(depends_on_question_value)
    const string_question_depends_on_value = (q?.depends_on_value)?.toString()?.toLowerCase()
    const string_depends_on_question_value = (depends_on_question_value)?.toString()?.toLowerCase()

    switch (q?.depends_on_type) {
      case QUOTE_FUNNEL_QUESTION_DEPENDS_ON_TYPE.EQUAL_TO:
        return string_depends_on_question_value === string_question_depends_on_value;
      case QUOTE_FUNNEL_QUESTION_DEPENDS_ON_TYPE.GREATER_THAN:
        return parsed_depends_on_value > parseFloat(q?.depends_on_value)
      case QUOTE_FUNNEL_QUESTION_DEPENDS_ON_TYPE.LESS_THAN:
        return parsed_depends_on_value < parseFloat(q?.depends_on_value)
      case QUOTE_FUNNEL_QUESTION_DEPENDS_ON_TYPE.NOT_EQUAL_TO:
        return string_depends_on_question_value !== string_question_depends_on_value;
      case QUOTE_FUNNEL_QUESTION_DEPENDS_ON_TYPE.NOT_NULL:
        return !!string_depends_on_question_value
      case QUOTE_FUNNEL_QUESTION_DEPENDS_ON_TYPE.INCLUDES_IN_ARRAY:
        return (string_depends_on_question_value?.split(', ') || []).includes(string_question_depends_on_value)
      default:
        return true; // No dependency, include the question
    }
  });
};

/**
 * Normalizes a value for timeframe group-by key comparison so that different
 * date formats (e.g. ISO string vs date-only) match when comparing current vs compare-to data.
 * @param {string} key - Group by key (e.g. start_of_day, start_of_week)
 * @param {*} value - Value to normalize
 * @returns {*} Normalized value for comparison
 */
const normalizeTimeframeKeyForComparison = (key, value) => {
  if (value == null || value === '') return value
  if (!ANALYTICS_TIMEFRAME_GROUP_BY_KEYS.includes(key)) return value
  if (key === 'hour') return value
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toISOString().slice(0, 10)
}

/**
 * Returns an object from the array which have same group by key values.
 * For timeframe keys (e.g. start_of_day), normalizes date values so that
 * comparison works when grouping by Date and showing compare-to data.
 * @param {Array} arr - Array of objects
 * @param {Object} key_vals - Object containing the group by key value pairs
 * @param {Array} possible_keys - Array containing the possible group by keys
 */
export const findbyGroupByKeysAdminAnalytics = (arr, key_vals, possible_keys) => {
  const normalizedKeyVals = { ...key_vals }
  possible_keys.forEach(k => {
    if (ANALYTICS_TIMEFRAME_GROUP_BY_KEYS.includes(k) && key_vals[k] != null) {
      normalizedKeyVals[k] = normalizeTimeframeKeyForComparison(k, key_vals[k])
    }
  })
  return arr?.find(elem => {
    const elemPicked = _.pick(elem, possible_keys)
    const normalizedElem = { ...elemPicked }
    possible_keys.forEach(k => {
      if (ANALYTICS_TIMEFRAME_GROUP_BY_KEYS.includes(k) && elemPicked[k] != null) {
        normalizedElem[k] = normalizeTimeframeKeyForComparison(k, elemPicked[k])
      }
    })
    return _.isEqual(normalizedElem, normalizedKeyVals)
  })
}
/**

Calculates the acceptance rate as a percentage.
@param {number} accepted - Number of accepted items
@param {number} total - Total number of items
@returns {number} The acceptance rate as a percentage EX: - [(accepted / total ) * 100]
*/
export const getSafePercentage = (accepted, total) => {
  if (!total) {
    return 0.0;
  }

  const checkedAccepted = accepted || 0; // Set accepted to 0 if falsy
  const checkedTotal = total || 1; // Set total to 1 if falsy to avoid division by zero

  return ((checkedAccepted / checkedTotal) * 100)
}

/**
@param {number} numerator - Total number of items
@param {number} denominator - Number of accepted items
@returns {number} EX: - (numerator / denominator)
*/
export const getSafeDivision = (numerator, denominator) => {
  if (!denominator) {
    return 0.0;
  }

  const checkedNumerator = numerator || 0;
  const checkedDenominator = denominator || 1;

  return (checkedNumerator / checkedDenominator);
}

export function compareBudgetTypes(budgetTypeA, budgetTypeB, order) {
  if (order === ORDER_BY.ASC) {
    if (budgetTypeA === BUDGETS_TYPES.DOLLAR) {
      return -1;
    } else if (budgetTypeB === BUDGETS_TYPES.DOLLAR) {
      return 1;
    } else if (budgetTypeA === BUDGETS_TYPES.VOLUME) {
      return -1;
    } else if (budgetTypeB === BUDGETS_TYPES.VOLUME) {
      return 1;
    } else {
      return 0; // No budgets or volumes, consider them equal
    }
  } else {
    // For descending order
    if (budgetTypeB === BUDGETS_TYPES.UNLIMITED) {
      return -1;
    } else if (budgetTypeA === BUDGETS_TYPES.UNLIMITED) {
      return 1;
    } else if (budgetTypeB === BUDGETS_TYPES.DOLLAR) {
      return 1;
    } else if (budgetTypeA === BUDGETS_TYPES.DOLLAR) {
      return -1;
    } else if (budgetTypeB === BUDGETS_TYPES.VOLUME) {
      return 1;
    } else if (budgetTypeA === BUDGETS_TYPES.VOLUME) {
      return -1;
    }
  }
}

const getConfigKeys = (str_list) => {
  const new_str_list = str_list.map(str => {
    const pattern = /config\.(\w+)/g;

    let matches = [];
    let match;

    while ((match = pattern.exec(str)) !== null) {
      matches.push(match[1]);
    }
    return matches;
  }
  )

  return _.uniq(_.flatten(new_str_list));
}

export const getConfigKeysFromIntegration = (integration) => {
  const req_posting_url_key_sources = [integration?.posting_url || '']
  const req_headers_key_sources = integration?.lead_integration_req_headers?.map(header => header?.value || '') || []
  const req_payloads_key_sources = integration?.lead_integration_req_payloads?.map(payload => payload?.data || '') || []

  return getConfigKeys([...req_posting_url_key_sources, ...req_headers_key_sources, ...req_payloads_key_sources])
}

export const isString = (s) => {
  return typeof (s) === 'string' || s instanceof String;
}

export const getStringifiedObject = (obj, value_if_invalid) => {
  if (obj == null) {
    return value_if_invalid;
  }

  if (isString(obj)) {
    return obj;
  }

  try {
    return JSON.stringify(obj);
  } catch (e) {
    return value_if_invalid;
  }
}

export function getKeyByValue(object, targetValue) {
  for (const key in object) {
    if (object.hasOwnProperty(key) && object[key] === targetValue) {
      return key;
    }
  }
  return null;
}


export function createDefaultColumnSortedObject(dataField, order) {
  return {
    dataField: dataField,
    order: order,
  };
}

export function toFixed(value, decimal) {
  if (!value) {
    return 0
  }
  if (typeof decimal !== 'number') {
    throw new Error('decimal argument must be a number');
  }
  return value.toFixed(decimal)
}

export const addPadStart = (value) => {
  if (!value) {
    return null
  }
  return value?.toString()?.padStart(5, '0')
};

export const convertStringToBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (value == null) return false;
  if (typeof value !== 'string') return false;

  const lowerValue = value.toLowerCase().trim();
  switch (lowerValue) {
    case 'true':
    case '1':
    case 'yes':
    case 'married':
      return true;
    default:
      return false;
  }
};

export const booleanToString = (value) => {
  if (value === true) {
    return 'true';
  } else if (value === false) {
    return 'false';
  }
  return value;
}

export const getCallStatusHTML = (status) => {
  switch (status) {
    case STATUS_TYPES.COMPLETED:
      return <span><i className='mdi mdi-circle font-size-12 sfgreen' /> Completed</span>;
    case STATUS_TYPES.FAILED:
      return <span><i className='mdi mdi-circle font-size-12 sfred' /> Failed</span>;
    case STATUS_TYPES.NO_ANSWER:
      return <span><i className='mdi mdi-circle font-size-12 sfred' /> No Answer</span>;
    case STATUS_TYPES.BUSY:
      return <span><i className='mdi mdi-circle font-size-12 sfred' /> Busy</span>;
    default:
      return <span><i className='mdi mdi-circle font-size-12 sfyellow' /> In Progress</span>;
  }
}
/**
 * Sorts an array of objects by date based
@param {array} array 
@param {string} key 
@returns array of objects sorted by date based on the key provided.
*/
export function sortByDateDESC(array, key) {
  try {
    return (array || []).sort((a, b) => new Date(b[key]) - new Date(a[key]));
  } catch (error) {
    console.error("Error sorting by date:", error);
    return array || [];
  }
}

/**
 * 
 * @param {number} cell milliseconds
 * @returns {number} seconds
 */
export function millisecondsToSecondsFormatter(cell) {
  const seconds = cell / 1000; // Convert milliseconds to seconds
  return seconds.toFixed(2); // Display seconds with two decimal places
}

/**
 * @brief Wrapper function to calculate percent increase
 * 
 * if a == 0, then return ''
 * @param {*} a 
 * @param {*} b 
 */
export const percentIncreaseOrEmpty = (a, b) => {
  if (!a || a == 0) {
    return ''
  }

  return percIncrease(a, b);
}

export const getAdGroupSfFilters = (sf_filters, campaigns, profile_id, campaign, current_account) => {
  if (sf_filters && campaigns.length > 0 && profile_id) {
    const lead_type_id = campaign?.lead_type?.id
    const product_type_id = campaign?.product_type?.id
    const is_an_ad_group_level_filter = "is_an_ad_group_level_filter";
    const adgroup_sf_filters = getSfFilterByProductAndLeadType(product_type_id, lead_type_id, is_an_ad_group_level_filter, sf_filters, current_account);
    return adgroup_sf_filters;
  }
}


export const leadTypeCheckForMotorCycle = (product_type_id) => {
  return (product_type_id === PRODUCT_TYPE_IDS.CLICKS);
}

/**
 * Filters lead types based on the product type and feature flags.
 * @param {number} product_type_id - The ID of the product type.
 * @param {Array} lead_types - Array of lead type objects.
 * @param {Object} feature_flags - Object containing feature flags.
 * @returns {Array} - Array of filtered lead type objects with value and label properties.
 */
export const filterLeadTypeOptions = (product_type_id, lead_types, feature_flags) => {
  let filteredLeadTypes = [];
  switch (product_type_id) {
    case PRODUCT_TYPE_IDS.LEADS:
      filteredLeadTypes = lead_types.filter(ele =>
        ele.id != LEAD_TYPE_IDS.MOTORCYCLE &&
        ele.id != LEAD_TYPE_IDS.P_C_BUNDLE
      )
      break;
    case PRODUCT_TYPE_IDS.CALLS:
      filteredLeadTypes = lead_types.filter(ele =>
        ele.id != LEAD_TYPE_IDS.MOTORCYCLE
      )
      break;
    case PRODUCT_TYPE_IDS.CLICKS:
      filteredLeadTypes = lead_types.filter(ele => {
        // Always filter out P_C_BUNDLE
        if (ele.id == LEAD_TYPE_IDS.P_C_BUNDLE) {
          return false;
        }
        if (ele.id == LEAD_TYPE_IDS.MOTORCYCLE) {
          return feature_flags?.motorcycle_lead_type;
        }
        return true;
      })
      break;
    case PRODUCT_TYPE_IDS.SYNDI_CLICKS:
      filteredLeadTypes = lead_types.filter(ele =>
        ele.id != LEAD_TYPE_IDS.MOTORCYCLE &&
        ele.id != LEAD_TYPE_IDS.P_C_BUNDLE
      )
      break;
    default:
      filteredLeadTypes = lead_types.filter(ele =>
        ele.id != LEAD_TYPE_IDS.MOTORCYCLE
      )
  }
  filteredLeadTypes = filteredLeadTypes.filter(lt => (lt.id !== LEAD_TYPE_IDS.GROUP_HEALTH || feature_flags.group_health_lead_type))
  return filteredLeadTypes.map(element => ({ value: element.id, label: capitalizeWord(element.name) }));
}

/**
 * Checks if a specific lead type is selected in the selectedLeadTypes array.
 * Returns true if "All Insurance Types" is selected (empty array) or if the specified lead type is in the selection.
 * @param {Array} selectedLeadTypes - Array of selected lead type objects with value property
 * @param {string} leadTypeName - Name of the lead type to check (from NAME_OF_LEAD_TYPES)
 * @returns {boolean} - True if the lead type is selected or all types are selected
 */
export const isLeadTypeSelected = (selectedLeadTypes, leadTypeName) => {
  if (!selectedLeadTypes || selectedLeadTypes.length === 0) {
    return true; // "All Insurance Types" is selected
  }
  const leadTypeId = getLeadTypeByName(leadTypeName)?.id;
  if (!leadTypeId) {
    return false;
  }
  return selectedLeadTypes.some(leadType => leadType?.value === leadTypeId);
}

// Function to generate last 30 days
export const generateLast30Days = () => {
  const result = [];
  for (let i = 0; i < 30; i++) {
    result.push(subDays(new Date(), i));
  }
  return result.reverse();
};

export const generateLast24Hours = () => {
  const result = [];
  for (let i = 0; i < 24; i++) {
    result.push(subHours(new Date(), i));
  }
  return result.reverse();
};

export const generateLastHour = () => {
  const result = [];
  for (let i = 0; i < 60; i++) {
    result.push(subMinutes(new Date(), i));
  }
  return result.reverse();
};

/**
 * Calculates the column order of a particular table
 * @param {Array} merged_columns - Columns in a particular tabe.
 * @param {Object} customize_order - Object containing order from backend.
 * @param {String} selectedProduct - Particular product if the page has product type options
  @returns object with order of every column in the merged_columns.
 * 
 */
export const calculateColumnOrders = (merged_columns, customize_order, selectedProduct = null) => {
  return (merged_columns || [])
    .filter((item) => item?.columnName)
    .map((item, index) => {
      const order = !!selectedProduct
        ? customize_order?.[selectedProduct]?.[item.columnName]
        : customize_order?.[item.columnName];

      return { [item.columnName]: (order || index) + 1 };
    })
    .reduce((prev, current) => ({ ...prev, ...current }), {});
};

export const filterLeadTypeOptionsforMotorCycleType = (leadTypeOptions, featureFlagEnabled, product_type_id, sendAsLabelValueFormat = true, isAllProductTypeSelected = false) => {
  const filteredOptions = (featureFlagEnabled && (product_type_id === PRODUCT_TYPE_IDS.CLICKS || isAllProductTypeSelected)) ? leadTypeOptions : leadTypeOptions?.filter((el) => el?.id !== LEAD_TYPE_IDS.MOTORCYCLE)
  if (!sendAsLabelValueFormat) {
    return filteredOptions
  }
  return filteredOptions.map(element => ({ value: element.id, label: capitalizeWord(element.name) }));

}

export const addLeadTypeIfNotPresent = (optionTypesState, leadTypeName) => {
  // Check if the lead type is not present
  if (optionTypesState[0]?.options.some((el) => el.label !== leadTypeName)) {
    // Add the lead type
    optionTypesState[0].options.push({
      label: leadTypeName, value: getLeadTypeByName(leadTypeName).id
    });
  }
  return optionTypesState;
};

export const checkTabsIdPresent = (tabs, activeTab) => {
  const isTabPresent = Object.values(tabs)?.length >= Number(activeTab)
  return isTabPresent ? activeTab : TABS_CONSTANTS.STATE_WISE_METRICS;
}

// Converts time from seconds to hours, minutes and remaining seconds
// Eg: I/P: 3700 => O/P: 1h 1m 40s
export const formatTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (remainingSeconds > 0 || parts.length === 0) parts.push(`${remainingSeconds}s`);

  return parts.join(' ');
};

export const generateConsentCaptureUrl = (certificate_id) => {
  return `${AppConfiguration.CONSENT_REPLAYER_URL}/certificateDetails/${certificate_id}`
}

export const getDropdownButtonLabelForBrands = ({ _placeholderButtonLabel, value,
}) => {
  if (value && value?.some(o => o.value === 'all')) {
    return (
      <span className="css-1v99tuv text-black align-self-center">All Brands</span>
    )
  }
  return (
    <span className="css-1v99tuv text-black align-self-center">
      {value?.length} selected
    </span>
  )
}


export const getProductTypeVariant = (product_type_id) => {
  switch (product_type_id) {
    case PRODUCT_TYPE_IDS.LEADS:
    case PRODUCT_TYPE_IDS.CALLS:
      return product_type_id;
    case PRODUCT_TYPE_IDS.CLICKS:
    case PRODUCT_TYPE_IDS.SYNDI_CLICKS:
    case PRODUCT_TYPE_IDS.QUOTE_FUNNELS:
      return PRODUCT_TYPE_IDS.CLICKS;
    default:
      return PRODUCT_TYPE_IDS.CLICKS;
  }
}

//returns brandsOptions based on the input event
export const getUpdatedOptionsFromEvent = (selectedOption, event, brandsOptions) => {
  const isAllOption = event?.option?.value === 'all'
  const isSelectAction = event?.action === 'select-option'
  const isDeselectAction = event?.action === 'deselect-option'

  if (isAllOption) {
    return isSelectAction ? brandsOptions : []
  }
  else if (isDeselectAction) {
    return selectedOption.filter(o => o.value !== 'all')
  }
  else if (selectedOption.length === brandsOptions.length - 1) {
    return brandsOptions
  }

  return selectedOption
};

//Filters data based on selectedBrands

/**
 * Filters data based on selectedBrands
 * @param {Array} data - Array of object containing individual invoices.
 * @param {Array} selectedBrands - Brands selected by user.
 * @param {String} filterKey - Key in the each invoices which has brand's value
  @returns Invoices whose brand's value is in selectedBrands array.
 * 
 */
export const filterDataFromBrandFilter = (data, selectedBrands, filterKey) => {
  if (!data?.length || !selectedBrands?.length) {
    return [];
  }
  return data.filter((el) => {
    const value = filterKey.includes('.')
      ? filterKey.split('.').reduce((obj, k) => obj?.[k], el) // Resolved issue with nested filterKey like el.brand.name
      : el?.[filterKey];

    return selectedBrands.some((brand) => brand?.value === value);
  });
};


export const filteredBrandIds = (selectedArr, brandList) => {
  const brandIds = (selectedArr.filter((el) => el.value !== 'all')).map(selected => {
    const matchingBrand = brandList.find(brand => brand.name === selected.value);
    return matchingBrand.id?.toString();
  })
  return brandIds;
}

export const getCampaignByCampaignId = ((campaigns, id) => campaigns.find((el) => el.id == id))

export const filteredConversionPixels = (conversion_pixels, show_rtb_ff, brand_id) => {
  return conversion_pixels.filter(pixel =>
    show_rtb_ff
      ? !!pixel.status
      : !!pixel.status && pixel.brand_id === brand_id
  )
}

/**
 * Updates the current host by replacing specific subdomains (admin or client) 
 * with the quotes subdomain as defined in the AppConfiguration.
 *
 * @returns {string} The updated host with the quotes subdomain.
 */
export const getUpdatedHostForQF = () => {
  const hostParts = window.location.host.split('.');
  return hostParts
    .map(part => {
      if (
        part.includes(AppConfiguration.REACT_ADMIN_HOST_SUBDOMAIN) ||
        part.includes(AppConfiguration.REACT_CLIENT_HOST_SUBDOMAIN)
      ) {
        return AppConfiguration.REACT_QUOTES_HOST_SUBDOMAIN;
      }
      return part;
    })
    .join('.');
};

export const sanitizeProspectFilters = (filters) => {
  return filters.filter(item => {
    if (item.component_type === FILTER_COMPONENT_TYPES.COMPARATOR) {
      return item.is_active && (!!item.value) && (item.comparator_value.trim() !== "");
    }
    return item.is_active && (Array.isArray(item.value) ? !!item.value.length : !!item.value);
  });
}

export const getUniqueOptions = (options, optionsMap) => {
  options.forEach(option => {
    const key = option.label;
    if (!optionsMap.has(key)) {
      optionsMap.set(key, option);
    }
  });
}

export const winRateCsvFormatter = (cellContent) => {
  return SfPercentStringFormatter(toFixed(cellContent * 100, 2) || 0)
}

/**
 * Filters SF filters based on feature flags and targeting settings
 * @param {Array} sfFilters - Array of SF filter objects
 * @param {boolean} isZipTiersAvailable - Flag to indicate if zip tiers are available
 * @param {boolean} isEnableSicEightTargeting - Flag to enable SIC8 targeting
 * @param {boolean} isEnableSicTargeting - Flag to enable SIC targeting
 * @param {boolean} isEnableNaicsTargeting - Flag to enable NAICS targeting
 * @returns {Array} Filtered and mapped options array
 */
export const getFilteredSfFilterOptions = (
  sfFilters,
  isZipTiersAvailable,
  isEnableSicEightTargeting,
  isEnableSicAndNaicsTargeting
) => {
  if (!sfFilters) return [];

  return sfFilters
    .filter(each => {
      if (!isZipTiersAvailable && each.name === ZIP_TIER) return false;
      if (!isEnableSicEightTargeting && each.name === SIC8_CODE) return false;
      if (!isEnableSicAndNaicsTargeting && [SIC_CODE, NAICS_CODE].includes(each.name)) return false;
      return true;
    })
    .map(filter => ({
      label: filter.name,
      value: filter,
    }));
};

export const percentageWithRatingFormatter = (value = 0) => {
  const dots = [1, 2, 3, 4, 5].map(dotIndex => (
    <i
      key={dotIndex}
      className={`bx bxs-circle me-1 fs-10 ${getPercentageDotColor(value, dotIndex)}`}
    />
  ));

  return (
    <div className='d-flex align-items-center'>
      <span className='me-1' style={{ width: "60px" }}>{value}%</span>
      {dots}
    </div>
  );
};


const getPercentageDotColor = (value, dotIndex) => {
  const grey = "text-muted";
  const yellow = "text-warning";
  const blue = "text-primary";
  const green = "text-success";

  // Number of filled dots based on percentage
  const filledDots = Math.round((value / 100) * 5);

  // Empty dots
  if (dotIndex > filledDots) return grey;

  // Color ranges
  if (value <= 40) return yellow;
  if (value <= 80) return blue;
  return green;
};

/**
 * Returns the data fields for visible report-table columns.
 *
 * @param {Array} columns - Array of column definitions from react-bootstrap-table2
 * @returns {Array} - Visible column dataField values
 */
export const getVisibleColumnDataFields = (columns = []) => columns
  .filter(column => {
    const isHidden = typeof column.hidden === 'function' ? column.hidden() : !!column.hidden;
    return !isHidden && column.dataField;
  })
  .map(column => column.dataField);

/**
 * Ensures CSV export respects the hidden property to match UI visibility.
 * Columns that are visible in the UI will be exported, and hidden columns will not.
 * Action columns (like 'options', 'campaign_notes') can be excluded from export.
 * 
 * @param {Array} columns - Array of column definitions from react-bootstrap-table2
 * @param {Array} columnsNeverExport - Optional array of column dataField names that should never be exported (e.g., ['options', 'campaign_notes'])
 * @returns {Array} - Modified columns array with csvExport set based on hidden property
 */
export const handleColumnsForCsvExport = (columns, columnsNeverExport = []) => {
  return columns.map((column) => {
    // Evaluate hidden property (could be boolean or function)
    const isHidden = typeof column.hidden === 'function' ? column.hidden() : !!column.hidden;

    // Columns that should never export
    if (column.dataField && columnsNeverExport.includes(column.dataField)) {
      return {
        ...column,
        csvExport: false,
      }
    }

    // For all other columns, export if visible (not hidden)
    // This ensures CSV export matches UI visibility exactly
    return {
      ...column,
      csvExport: !isHidden,
    }
  })
}

// Helper function to get authentication headers
export const getAuthHeaders = () => {
  const adminUser = bsh_getAdminUserDetails();
  const clientUser = bsh_getClientUserDetails();

  const isClient = !!clientUser?.token;
  const isAdmin = !!adminUser?.token;

  if (!isAdmin && !isClient) {
    throw new Error("Authentication token is required. Please log in.");
  }

  const headers = {
    "Content-Type": "application/json",
  };

  if (isClient) {
    headers.Authorization = clientUser.token;
    if (clientUser?.account_id) {
      headers["account-id"] = clientUser.account_id;
    }
  } else if (isAdmin) {
    headers.Authorization = adminUser.token;
  }
  return headers;
};

export const getQALabel = () => {
  switch (AppConfiguration?.PRODUCT_NAME) {
    case WHITE_LABELED_PRODUCT_NAMES.INSURANCE_IO:
      return "Powered by SmartFinancial"
    case WHITE_LABELED_PRODUCT_NAMES.GOAL:
      return "Powered by Goal"
    case WHITE_LABELED_PRODUCT_NAMES.BENEPATH:
      return "Powered by Benepath"
  }
}

const getValueByDataField = (row, dataField) => {
  if (!row || !dataField) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, dataField)) return row[dataField];

  const fieldPath = String(dataField)
    .replace(/\?\./g, '.')
    .replace(/\[([^\]]+)\]/g, '.$1')
    .split('.')
    .filter(Boolean);

  return _.get(row, fieldPath);
}

export const sortDataByColumn = (data, sortState) => {
  if (!sortState?.field || !data?.length) return data;
  return [...data].sort((rowA, rowB) => {
    const a = getValueByDataField(rowA, sortState.field);
    const b = getValueByDataField(rowB, sortState.field);
    const aFloat = parseFloat(a);
    const bFloat = parseFloat(b);
    if (!isNaN(aFloat) && !isNaN(bFloat)) {
      return sortState.order === 'asc' ? aFloat - bFloat : bFloat - aFloat;
    }
    const cmp = String(a ?? '').localeCompare(String(b ?? ''), undefined, { sensitivity: 'base', numeric: true });
    return sortState.order === 'asc' ? cmp : -cmp;
  });
};
